// pages/AccountDetail.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Trash2,
  Mail,
  Phone,
  MapPin,
  Building2,
  User,
  MoreVertical,
  FileText,
  Paperclip,
  Activity,
  DollarSign,
  Users,
  ExternalLink,
  Plus,
  Package,
  Briefcase,
  Globe,
  AlertCircle,
  Calendar,
  CheckSquare,
  Save,
  X as CloseIcon,
  Loader,
  XCircle,
  CheckCircle,
  Wifi,
  PhoneCall,
  ClipboardList,
  FileImage,
  FileVideo,
  FileAudio,
  FileSpreadsheet,
  FilePlus,
  FileArchive,
  File,
} from "lucide-react";
import apiService from "../../services/api";
import EditAccountModal from "../../components/EditAccountModal";
import CreateContactModal from "../../components/CreateContactModal";
import CreateOpportunityModal from "../../components/CreateOpportunityModal";
import CreateProjectModal from "../../components/CreateProjectModal";
import CreateCaseModal from "../../components/CreateCaseModal";
import CreateVisitModal from "../../components/CreateVisitModal";
import CreateBroadbandInfoModal from "../../components/CreateBroadbandInfoModal";
import CreatePhoneSystemModal from "../../components/CreatePhoneSystemModal";
import CreateLineInfoModal from "../../components/CreateLineInfoModal";
import CreateAssetModal from "../../components/CreateAssetModal";
import BookInstallModal from "../../components/BookInstallModal";
import CreateTaskModal from "../../components/CreateTaskModal";
import SendAccountEmailModal from "../../components/SendAccountEmailModal";
import AccountEmailThread from "../../components/AccountEmailThread";
import AccountDuplicatesSidebar from "../../components/AccountDuplicatesSidebar";
import PhoneLink from "../../components/PhoneLink";
import CallNotesTimeline from "../../components/CallNotesTimeline";
import { useTheme } from "../../contexts/ThemeContext";

// Extracted EditableField component - defined OUTSIDE the parent component
const EditableField = ({
  label,
  field,
  value,
  type = "text",
  options = null,
  readOnly = false,
  onSave,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const { isDarkMode } = useTheme();

  // Format value for editing
  const getEditValue = useCallback(
    (val) => {
      if (type === "date" && val) {
        try {
          const date = new Date(val);
          return date.toISOString().split("T")[0];
        } catch {
          return val || "";
        }
      }
      return val || "";
    },
    [type],
  );

  // Format value for display
  const getDisplayValue = useCallback(
    (val) => {
      if (type === "date" && val) {
        try {
          return new Date(val).toLocaleDateString();
        } catch {
          return val;
        }
      }
      return val || "—";
    },
    [type],
  );

  // Start editing
  const startEditing = useCallback(() => {
    setLocalValue(getEditValue(value));
    setIsEditing(true);
  }, [value, getEditValue]);

  // Cancel editing
  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setLocalValue("");
  }, []);

  // Save changes
  const saveChanges = useCallback(async () => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      await onSave(field, localValue);
      setIsEditing(false);
      setLocalValue("");
    } catch (error) {
      console.error("Error saving field:", error);
      alert("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  }, [field, localValue, onSave, isSaving]);

  // Handle keyboard events
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        cancelEditing();
      } else if (e.key === "Enter" && type !== "textarea") {
        e.preventDefault();
        saveChanges();
      } else if (e.key === "Enter" && e.ctrlKey && type === "textarea") {
        e.preventDefault();
        saveChanges();
      }
    },
    [cancelEditing, saveChanges, type],
  );

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Move cursor to end for text inputs
          if (
            type === "text" ||
            type === "textarea" ||
            type === "email" ||
            type === "tel" ||
            type === "url"
          ) {
            const len = inputRef.current.value.length;
            inputRef.current.setSelectionRange(len, len);
          }
        }
      });
    }
  }, [isEditing, type]);

  // Handle click outside to cancel
  useEffect(() => {
    if (!isEditing) return;

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        cancelEditing();
      }
    };

    // Delay adding listener to avoid immediate trigger
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditing, cancelEditing]);

  if (readOnly) {
    return (
      <div>
        <label
          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} block mb-1`}
        >
          {label}
        </label>
        <p className={isDarkMode ? "text-white" : "text-gray-900"}>
          {getDisplayValue(value)}
        </p>
      </div>
    );
  }

  return (
    <div className="group" ref={containerRef}>
      {label && (
        <label
          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} block mb-1`}
        >
          {label}
        </label>
      )}
      <div className="flex items-center gap-2">
        {isEditing ? (
          <div className="flex-1 flex items-center gap-2">
            {options ? (
              <select
                ref={inputRef}
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
              >
                <option value="">Select...</option>
                {options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : type === "textarea" ? (
              <textarea
                ref={inputRef}
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 resize-none ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                rows={3}
              />
            ) : type === "date" ? (
              <input
                ref={inputRef}
                type="date"
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
              />
            ) : (
              <input
                ref={inputRef}
                type={type}
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
              />
            )}
            <button
              onClick={saveChanges}
              disabled={isSaving}
              className="p-2 bg-green-600 hover:bg-green-700 rounded transition-colors disabled:opacity-50"
              title="Save"
            >
              {isSaving ? (
                <Loader size={16} className="animate-spin" />
              ) : (
                <Save size={16} />
              )}
            </button>
            <button
              onClick={cancelEditing}
              disabled={isSaving}
              className="p-2 bg-gray-700 hover:bg-gray-600 rounded transition-colors disabled:opacity-50"
              title="Cancel"
            >
              <CloseIcon size={16} />
            </button>
          </div>
        ) : (
          <>
            <div
              className={`flex-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              {getDisplayValue(value)}
            </div>
            <button
              onClick={startEditing}
              className={`opacity-0 group-hover:opacity-100 p-1.5 rounded transition-all ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
              title="Edit"
            >
              <Edit size={14} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const AccountDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [activeRelatedTab, setActiveRelatedTab] = useState("contacts");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [tasks, setTasks] = useState([]);
  // Account Status
  const [showStatusConfirmation, setShowStatusConfirmation] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [statusReason, setStatusReason] = useState("");
  const [statusNotes, setStatusNotes] = useState("");

  // Modal states
  const [showCreateContact, setShowCreateContact] = useState(false);
  const [showCreateOpportunity, setShowCreateOpportunity] = useState(false);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [showCreateCase, setShowCreateCase] = useState(false);
  const [showCreateVisit, setShowCreateVisit] = useState(false);
  const [showCreateBroadband, setShowCreateBroadband] = useState(false);
  const [showCreateLineInfo, setShowCreateLineInfo] = useState(false);
  const [showCreateAsset, setShowCreateAsset] = useState(false);
  const [showBookInstall, setShowBookInstall] = useState(false);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showSendEmail, setShowSendEmail] = useState(false);
  const [files, setFiles] = useState([]);
  // Related data
  const [notes, setNotes] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [cases, setCases] = useState([]);
  const [visits, setVisits] = useState([]);
  const [broadbandInfos, setBroadbandInfos] = useState([]);
  const [lineInfos, setLineInfos] = useState([]);
  const [assets, setAssets] = useState([]);
  const [appointments, setAppointments] = useState([]);

  // Note creation
  const [newNote, setNewNote] = useState("");
  const [newNoteTitle, setNewNoteTitle] = useState("");
  const [isPrivateNote, setIsPrivateNote] = useState(false);

  // Drag and drop
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const [filesToUpload, setFilesToUpload] = useState(null);
  const [showUploadConfirmation, setShowUploadConfirmation] = useState(false);
  const [users, setUsers] = useState([]);
  const [phoneSystems, setPhoneSystems] = useState([]);
  const [showCreatePhoneSystem, setShowCreatePhoneSystem] = useState(false);
  // Account Status definitions
  const accountStatuses = [
    { key: "active", label: "Active", color: "green" },
    { key: "inactive", label: "Inactive", color: "yellow" },
    { key: "suspended", label: "Suspended", color: "orange" },
    { key: "dead", label: "Dead", color: "red" },
  ];

  const fetchUsers = async () => {
    try {
      const response = await apiService.getCompanyUsers();
      setUsers(response.data || []);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };
  const fetchPhoneSystems = async () => {
    try {
      const response = await apiService.getAccountPhoneSystems(id);
      setPhoneSystems(response.data || []);
    } catch (error) {
      console.error("Error fetching phone systems:", error);
    }
  };
  useEffect(() => {
    fetchAccount();
    fetchUsers();
    fetchNotes();
    fetchFiles();
    fetchAttachments();
    fetchProjects();
    fetchVisits();
    fetchBroadbandInfos();
    fetchLineInfos();
    fetchAssets();
    fetchTasks();
    fetchAppointments();
    fetchPhoneSystems();
  }, [id]);
  const fetchTasks = async () => {
    try {
      const response = await apiService.getTasksByAccount(id);
      setTasks(response.data || []);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
  };
  const fetchAccount = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAccount(id);
      setAccount(response.data);
      if (response.data.contacts) setContacts(response.data.contacts);
      if (response.data.opportunities)
        setOpportunities(response.data.opportunities);
      if (response.data.cases) setCases(response.data.cases);
      if (response.data.tasks) setTasks(response.data.tasks);
    } catch (error) {
      console.error("Error fetching account:", error);
      navigate("/accounts");
    } finally {
      setLoading(false);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await apiService.getAccountNotes(id);
      setNotes(response.data || []);
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  };

  const fetchAttachments = async () => {
    try {
      const response = await apiService.getAccountAttachments(id);
      setAttachments(response.data || []);
    } catch (error) {
      console.error("Error fetching attachments:", error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await apiService.getProjectsByAccount(id);
      setProjects(response.data || []);
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  const fetchVisits = async () => {
    try {
      const response = await apiService.getVisitsByAccount(id);
      setVisits(response.data || []);
    } catch (error) {
      console.error("Error fetching visits:", error);
    }
  };

  const fetchBroadbandInfos = async () => {
    try {
      const response = await apiService.getBroadbandInfosByAccount(id);
      setBroadbandInfos(response.data || []);
    } catch (error) {
      console.error("Error fetching broadband infos:", error);
    }
  };

  const fetchLineInfos = async () => {
    try {
      const response = await apiService.getLineInfosByAccount(id);
      setLineInfos(response.data || []);
    } catch (error) {
      console.error("Error fetching line infos:", error);
    }
  };

  const fetchAssets = async () => {
    try {
      const response = await apiService.getAssetsByAccount(id);
      setAssets(response.data || []);
    } catch (error) {
      console.error("Error fetching assets:", error);
    }
  };

  const fetchAppointments = async () => {
    try {
      const response = await apiService.getAccountAppointments(id);
      setAppointments(response.data || []);
    } catch (error) {
      console.error("Error fetching appointments:", error);
    }
  };

  const handleStatusClick = (newStatus) => {
    if (
      newStatus === "inactive" ||
      newStatus === "dead" ||
      newStatus === "suspended"
    ) {
      setPendingStatus(newStatus);
      setStatusReason("");
      setStatusNotes("");
      setShowStatusConfirmation(true);
    } else {
      handleStatusChange(newStatus);
    }
  };

  const handleStatusChange = async (newStatus, reason = "", notes = "") => {
    try {
      const updateData = {
        accountStatus: newStatus,
        accountStatusReason: reason || null,
        accountStatusNotes: notes || null,
        accountStatusChangedAt: new Date().toISOString(),
      };

      await apiService.updateAccount(id, updateData);
      setShowStatusConfirmation(false);
      setPendingStatus(null);
      setStatusReason("");
      setStatusNotes("");
      await fetchAccount();
    } catch (error) {
      console.error("Error updating account status:", error);
      alert("Failed to update account status");
    }
  };

  const confirmStatusChange = () => {
    if (
      (pendingStatus === "inactive" ||
        pendingStatus === "dead" ||
        pendingStatus === "suspended") &&
      !statusReason.trim()
    ) {
      alert("Please provide a reason for the status change");
      return;
    }
    handleStatusChange(pendingStatus, statusReason, statusNotes);
  };

  const cancelStatusChange = () => {
    setShowStatusConfirmation(false);
    setPendingStatus(null);
    setStatusReason("");
    setStatusNotes("");
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this account?")) {
      try {
        await apiService.deleteAccount(id);
        navigate("/accounts");
      } catch (error) {
        console.error("Error deleting account:", error);
        if (error.response?.data?.relatedRecords) {
          alert(
            `Cannot delete account. It has:\n` +
              `${error.response.data.relatedRecords.contacts} contacts\n` +
              `${error.response.data.relatedRecords.opportunities} opportunities\n` +
              `${error.response.data.relatedRecords.cases} cases\n` +
              `${error.response.data.relatedRecords.projects} projects`,
          );
        } else {
          alert("Failed to delete account");
        }
      }
    }
  };

  const handleCreateNote = async () => {
    if (!newNote.trim()) return;

    try {
      await apiService.createAccountNote(id, {
        title: newNoteTitle,
        body: newNote,
        isPrivate: isPrivateNote,
      });
      setNewNote("");
      setNewNoteTitle("");
      setIsPrivateNote(false);
      fetchNotes();
    } catch (error) {
      console.error("Error creating note:", error);
      alert("Failed to create note");
    }
  };

  const fetchFiles = async () => {
    try {
      const response = await apiService.getAccountFiles(id);
      const filesData = response.data || [];

      // Sort files by date (latest first)
      const sortedFiles = filesData.sort((a, b) => {
        const dateA = a.latestVersion?.salesforceCreatedDate || a.createdAt;
        const dateB = b.latestVersion?.salesforceCreatedDate || b.createdAt;
        return new Date(dateB) - new Date(dateA);
      });

      setFiles(sortedFiles);
    } catch (error) {
      console.error("Error fetching files:", error);
    }
  };

  // Update file upload handler
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      await apiService.uploadAccountFile(id, formData);
      fetchFiles(); // Changed from fetchAttachments
      event.target.value = ""; // Reset input
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Failed to upload file");
    }
  };

  // Add delete handler
  const handleDeleteFile = async (fileId) => {
    if (!window.confirm("Are you sure you want to delete this file?")) return;

    try {
      await apiService.deleteAccountFile(id, fileId);
      fetchFiles();
    } catch (error) {
      console.error("Error deleting file:", error);
      alert("Failed to delete file");
    }
  };

  // Update drag and drop confirm upload
  const confirmUpload = async () => {
    if (!filesToUpload) return;

    try {
      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i];
        const formData = new FormData();
        formData.append("file", file);
        await apiService.uploadAccountFile(id, formData);
      }
      fetchFiles(); // Changed from fetchAttachments
      setShowUploadConfirmation(false);
      setFilesToUpload(null);
    } catch (error) {
      console.error("Error uploading files:", error);
      alert("Failed to upload files");
    }
  };

  // Helper function for file size formatting
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  // Updated helper function for file icon with Lucide components
  const getFileIconComponent = (fileType) => {
    const type = fileType?.toLowerCase() || "";

    if (
      type.includes("image") ||
      type.includes("png") ||
      type.includes("jpg") ||
      type.includes("jpeg") ||
      type.includes("gif")
    ) {
      return <FileImage size={20} className="text-purple-400" />;
    }
    if (
      type.includes("video") ||
      type.includes("mp4") ||
      type.includes("avi")
    ) {
      return <FileVideo size={20} className="text-pink-400" />;
    }
    if (
      type.includes("audio") ||
      type.includes("mp3") ||
      type.includes("wav")
    ) {
      return <FileAudio size={20} className="text-green-400" />;
    }
    if (type.includes("pdf")) {
      return <FileText size={20} className="text-red-400" />;
    }
    if (
      type.includes("word") ||
      type.includes("document") ||
      type.includes("doc")
    ) {
      return <FileText size={20} className="text-blue-400" />;
    }
    if (
      type.includes("sheet") ||
      type.includes("excel") ||
      type.includes("xls") ||
      type.includes("csv")
    ) {
      return <FileSpreadsheet size={20} className="text-green-500" />;
    }
    if (
      type.includes("presentation") ||
      type.includes("powerpoint") ||
      type.includes("ppt")
    ) {
      return <FilePlus size={20} className="text-orange-400" />;
    }
    if (
      type.includes("zip") ||
      type.includes("rar") ||
      type.includes("archive")
    ) {
      return <FileArchive size={20} className="text-yellow-400" />;
    }
    return <File size={20} className="text-gray-400" />;
  };

  // Helper function to construct full file URLs
  const getFullFileUrl = (fileUrl) => {
    if (!fileUrl) return null;

    // If it's already a full URL (starts with http:// or https://)
    if (fileUrl.startsWith("http://") || fileUrl.startsWith("https://")) {
      return fileUrl;
    }

    // Otherwise, prepend the S3 base URL
    const S3_BASE_URL = "https://fibre-crm.s3.eu-west-2.amazonaws.com/";
    return S3_BASE_URL + fileUrl;
  };

  // Helper function to get the proper creation date
  const getFileCreatedDate = (file) => {
    // Use salesforceCreatedDate from latestVersion if available
    if (file.latestVersion?.salesforceCreatedDate) {
      return new Date(file.latestVersion.salesforceCreatedDate);
    }
    // Otherwise fallback to the file's createdAt
    return new Date(file.createdAt);
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => prev + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => prev - 1);
    if (dragCounter - 1 === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setDragCounter(0);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      setFilesToUpload(files);
      setShowUploadConfirmation(true);
    }
  };

  const cancelUpload = () => {
    setShowUploadConfirmation(false);
    setFilesToUpload(null);
  };

  const handleFieldSave = async (field, value) => {
    // Handle nested customerPassword field
    if (field === "customerPassword") {
      const updateData = {
        customFields: {
          ...account.customFields,
          salesforce: {
            ...account.customFields?.salesforce,
            customerPassword: value,
          },
        },
      };
      await apiService.updateAccount(id, updateData);
    } else {
      await apiService.updateAccount(id, { [field]: value });
    }

    await fetchAccount();
  };

  const getAccountStatusColor = (status) => {
    const colors = {
      active: "text-green-400 bg-green-400/10 border-green-400/20",
      inactive: "text-yellow-400 bg-yellow-400/10 border-yellow-400/20",
      suspended: "text-orange-400 bg-orange-400/10 border-orange-400/20",
      dead: "text-red-400 bg-red-400/10 border-red-400/20",
    };
    return colors[status] || "text-gray-400 bg-gray-400/10 border-gray-400/20";
  };

  const getInitials = (name) => {
    return name
      ? name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .substring(0, 2)
      : "??";
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-[#0A0B0F]" : "bg-[#F4F5F7]"} flex items-center justify-center`}
      >
        <Loader
          className={`${isDarkMode ? "text-white" : "text-gray-900"} animate-spin`}
          size={32}
        />
      </div>
    );
  }

  if (!account) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-[#0A0B0F]" : "bg-[#F4F5F7]"} flex items-center justify-center`}
      >
        <div className={isDarkMode ? "text-white" : "text-gray-900"}>
          Account not found
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${isDarkMode ? "bg-[#0A0B0F] text-white" : "bg-[#F4F5F7] text-gray-900"}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Status Confirmation Modal */}
      {showStatusConfirmation && pendingStatus && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className={`rounded-lg border max-w-md w-full p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
          >
            <div className="flex items-start gap-3 mb-4">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  pendingStatus === "dead"
                    ? "bg-red-500/10"
                    : pendingStatus === "suspended"
                      ? "bg-orange-500/10"
                      : "bg-yellow-500/10"
                }`}
              >
                {pendingStatus === "dead" ? (
                  <XCircle className="text-red-400" size={24} />
                ) : pendingStatus === "suspended" ? (
                  <AlertCircle className="text-orange-400" size={24} />
                ) : (
                  <AlertCircle className="text-yellow-400" size={24} />
                )}
              </div>
              <div className="flex-1">
                <h3
                  className={`text-lg font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Confirm Status Change
                </h3>
                <p
                  className={`text-sm mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {pendingStatus === "dead"
                    ? "Mark this account as Dead? This indicates the account is no longer viable."
                    : pendingStatus === "suspended"
                      ? "Mark this account as Suspended? This indicates the account has been temporarily suspended."
                      : "Mark this account as Inactive? This indicates the account is temporarily not active."}
                </p>

                <div className="space-y-3">
                  <div>
                    <label
                      className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"} block mb-2`}
                    >
                      Reason *
                    </label>
                    <input
                      type="text"
                      value={statusReason}
                      onChange={(e) => setStatusReason(e.target.value)}
                      placeholder="e.g., Lost to competitor, Out of business"
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                      autoFocus
                    />
                  </div>

                  <div>
                    <label
                      className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"} block mb-2`}
                    >
                      Additional Notes (Optional)
                    </label>
                    <textarea
                      value={statusNotes}
                      onChange={(e) => setStatusNotes(e.target.value)}
                      placeholder="Any additional context..."
                      rows={3}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 resize-none ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                    />
                  </div>

                  <div
                    className={`p-3 rounded-lg ${isDarkMode ? "bg-gray-800/50" : "bg-gray-50"}`}
                  >
                    <div className="flex items-center justify-between text-sm">
                      <span
                        className={
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }
                      >
                        Current Status:
                      </span>
                      <span
                        className={`px-2 py-1 rounded ${getAccountStatusColor(account.accountStatus)}`}
                      >
                        {account.accountStatus?.charAt(0).toUpperCase() +
                          account.accountStatus?.slice(1)}
                      </span>
                    </div>
                    <div className="flex items-center justify-center my-2">
                      <ArrowLeft
                        className={`${isDarkMode ? "text-gray-600" : "text-gray-400"} rotate-180`}
                        size={20}
                      />
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span
                        className={
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }
                      >
                        New Status:
                      </span>
                      <span
                        className={`px-2 py-1 rounded ${getAccountStatusColor(pendingStatus)}`}
                      >
                        {pendingStatus.charAt(0).toUpperCase() +
                          pendingStatus.slice(1)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={cancelStatusChange}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              >
                Cancel
              </button>
              <button
                onClick={confirmStatusChange}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                  pendingStatus === "dead"
                    ? "bg-red-600 hover:bg-red-700"
                    : pendingStatus === "suspended"
                      ? "bg-orange-600 hover:bg-orange-700"
                      : "bg-yellow-600 hover:bg-yellow-700"
                }`}
              >
                Confirm Change
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div
        className={`border-b transition-colors ${isDarkMode ? "border-gray-700 bg-[#13161B]" : "border-gray-200 bg-white"}`}
      >
        <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-4">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <button
              onClick={() => navigate(-1)}
              className={`flex items-center gap-2 transition-colors ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
            >
              <ArrowLeft size={20} />
              <span>Go Back</span>
            </button>

            <div className="flex items-center gap-2 flex-wrap justify-end">
              <button
                onClick={() => setShowBookInstall(true)}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-lg transition-colors shadow-lg shadow-blue-600/20"
              >
                <Calendar size={16} />
                <span className="hidden sm:inline">Book Installation</span>
              </button>

              <button
                onClick={() => setShowCreateContact(true)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              >
                <User size={16} />
                <span className="hidden sm:inline">New Contact</span>
              </button>

              <button
                onClick={() => setShowCreateProject(true)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              >
                <Briefcase size={16} />
                <span className="hidden sm:inline">New Project</span>
              </button>

              <button
                onClick={() => setShowEditModal(true)}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                <Edit size={16} />
                <span className="hidden sm:inline">Edit</span>
              </button>

              <div className="relative">
                <button
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  className={`p-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
                >
                  <MoreVertical size={20} />
                </button>

                {showActionsMenu && (
                  <div
                    className={`absolute right-0 top-12 w-48 border rounded-lg shadow-xl z-50 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
                  >
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        setShowActionsMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left flex items-center gap-2 ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
                    >
                      <FileText size={16} />
                      Copy Link
                    </button>
                    <div
                      className={`border-t my-1 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                    ></div>
                    <button
                      onClick={() => {
                        handleDelete();
                        setShowActionsMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left flex items-center gap-2 text-red-400 ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Account Header Info */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                  {getInitials(account.name)}
                </div>
                <div>
                  <h1
                    className={`text-xl sm:text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {account.name}
                  </h1>
                  {account.accountNumber && (
                    <div
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      {account.accountNumber}
                    </div>
                  )}
                </div>

                {account.type && (
                  <span className="px-3 py-1 rounded-lg border text-sm font-medium bg-blue-500/10 text-blue-400 border-blue-500/20">
                    {account.type}
                  </span>
                )}

                {account.accountStatus && (
                  <span
                    className={`px-3 py-1 rounded-lg border text-sm font-medium ${getAccountStatusColor(account.accountStatus)}`}
                  >
                    {account.accountStatus.charAt(0).toUpperCase() +
                      account.accountStatus.slice(1)}
                  </span>
                )}
              </div>

              <div
                className={`flex flex-wrap items-center gap-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {account.industry && (
                  <div className="flex items-center gap-2">
                    <Briefcase size={16} />
                    <span>{account.industry}</span>
                  </div>
                )}
                {account.phone && (
                  <PhoneLink
                    phoneNumber={account.phone}
                    entityType="account"
                    entityId={account.id}
                    entityName={account.name}
                  />
                )}
                {account.website && (
                  <a
                    href={account.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-2 transition-colors ${isDarkMode ? "hover:text-blue-400" : "hover:text-blue-600"}`}
                  >
                    <Globe size={16} />
                    <span>{account.website}</span>
                  </a>
                )}
                {(account.billingCity || account.billingState) && (
                  <div className="flex items-center gap-2">
                    <MapPin size={16} />
                    <span>
                      {[account.billingCity, account.billingState]
                        .filter(Boolean)
                        .join(", ")}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {account.owner && (
              <div className="flex flex-row sm:flex-col gap-3 flex-wrap">
                <div
                  className={`flex items-center gap-3 rounded-lg p-3 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"}`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? "bg-gray-700" : "bg-gray-300"}`}
                  >
                    {account.owner.profileImageUrl ? (
                      <img
                        src={account.owner.profileImageUrl}
                        alt={account.owner.name}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <span
                        className={`text-lg font-medium ${isDarkMode ? "text-white" : "text-gray-700"}`}
                      >
                        {account.owner.name?.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div>
                    <div
                      className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Account Owner
                    </div>
                    <div
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {account.owner.name}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Account Status Change Section */}
          <div
            className={`mt-4 rounded-lg p-4 ${isDarkMode ? "bg-gray-800/30" : "bg-gray-100"}`}
          >
            <h3
              className={`text-sm font-medium mb-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Account Status
            </h3>
            <div className="grid grid-cols-2 sm:flex sm:items-center gap-3">
              {accountStatuses.map((status) => {
                const isActive = account.accountStatus === status.key;
                return (
                  <button
                    key={status.key}
                    onClick={() => handleStatusClick(status.key)}
                    className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                      isActive
                        ? status.key === "active"
                          ? "bg-green-500/20 border-green-500 text-green-400"
                          : status.key === "inactive"
                            ? "bg-yellow-500/20 border-yellow-500 text-yellow-400"
                            : status.key === "suspended"
                              ? "bg-orange-500/20 border-orange-500 text-orange-400"
                              : "bg-red-500/20 border-red-500 text-red-400"
                        : isDarkMode
                          ? "bg-gray-800/50 border-gray-700 text-gray-400 hover:border-gray-600"
                          : "bg-white border-gray-300 text-gray-500 hover:border-gray-400"
                    }`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isActive
                            ? status.key === "active"
                              ? "bg-green-500"
                              : status.key === "inactive"
                                ? "bg-yellow-500"
                                : status.key === "suspended"
                                  ? "bg-orange-500"
                                  : "bg-red-500"
                            : isDarkMode
                              ? "bg-gray-700"
                              : "bg-gray-300"
                        }`}
                      >
                        {isActive && <CheckCircle size={16} />}
                      </div>
                      <span className="text-sm font-medium">
                        {status.label}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            {account.accountStatusReason && (
              <div
                className={`mt-3 p-3 rounded-lg ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"}`}
              >
                <div
                  className={`text-xs mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Status Reason
                </div>
                <div
                  className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {account.accountStatusReason}
                </div>
                {account.accountStatusNotes && (
                  <>
                    <div
                      className={`text-xs mt-2 mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Notes
                    </div>
                    <div
                      className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                    >
                      {account.accountStatusNotes}
                    </div>
                  </>
                )}
                {account.accountStatusChangedAt && (
                  <div
                    className={`text-xs mt-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Changed:{" "}
                    {new Date(
                      account.accountStatusChangedAt,
                    ).toLocaleDateString()}
                  </div>
                )}
              </div>
            )}
          </div>

          {account.notes?.some((note) => note.title === "Account Merged") && (
            <div className="mt-4 bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="text-blue-400" size={20} />
                <h4 className="font-medium text-blue-400">Merge History</h4>
              </div>
              {account.notes
                .filter((note) => note.title === "Account Merged")
                .map((note) => (
                  <div
                    key={note.id}
                    className={`text-sm mt-2 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                  >
                    {note.body}
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tabs */}
            <div
              className={`rounded-lg border ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <div
                className={`flex border-b overflow-x-auto scrollbar-hide ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
              >
                {[
                  { id: "details", label: "Details", icon: FileText },
                  {
                    id: "related",
                    label: "Related",
                    icon: Activity,
                    count:
                      contacts.length +
                      opportunities.length +
                      projects.length +
                      cases.length +
                      visits.length +
                      broadbandInfos.length +
                      lineInfos.length +
                      assets.length +
                      phoneSystems.length +
                      tasks.length,
                  },
                  {
                    id: "appointments",
                    label: "Appointments",
                    icon: Calendar,
                    count: appointments.length || undefined,
                  },
                  {
                    id: "calls",
                    label: "Call History",
                    icon: Paperclip,
                  },
                  {
                    id: "files",
                    label: "Files",
                    icon: Paperclip,
                    count: files.length,
                  },
                  { id: "emails", label: "Emails", icon: Mail },
                ].map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-4 sm:px-6 py-3 font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                        activeTab === tab.id
                          ? "text-blue-400 border-b-2 border-blue-400"
                          : isDarkMode
                            ? "text-gray-400 hover:text-white"
                            : "text-gray-500 hover:text-gray-900"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon size={16} />
                        {tab.label}
                        {tab.count > 0 && (
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}
                          >
                            {tab.count}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="p-4 sm:p-6">
                {/* Details Tab */}
                {activeTab === "details" && (
                  <div className="space-y-6">
                    {/* Account Information */}
                    <div>
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <Building2 size={18} />
                        Account Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <EditableField
                          label="Account Name"
                          field="name"
                          value={account.name}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Account Number"
                          field="accountNumber"
                          value={account.accountNumber}
                          readOnly
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Type"
                          field="type"
                          value={account.type}
                          options={[
                            { value: "Customer", label: "Customer" },
                            { value: "Prospect", label: "Prospect" },
                            { value: "Partner", label: "Partner" },
                            { value: "Competitor", label: "Competitor" },
                          ]}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Industry"
                          field="industry"
                          value={account.industry}
                          options={[
                            { value: "Technology", label: "Technology" },
                            { value: "Finance", label: "Finance" },
                            { value: "Healthcare", label: "Healthcare" },
                            { value: "Retail", label: "Retail" },
                            { value: "Manufacturing", label: "Manufacturing" },
                          ]}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Phone"
                          field="phone"
                          value={account.phone}
                          type="tel"
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Website"
                          field="website"
                          value={account.website}
                          type="url"
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Annual Revenue"
                          field="annualRevenue"
                          value={account.annualRevenue}
                          type="number"
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Employees"
                          field="numberOfEmployees"
                          value={account.numberOfEmployees}
                          type="number"
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Customer Password"
                          field="customerPassword"
                          value={
                            account.customFields?.salesforce?.customerPassword
                          }
                          type="text"
                          onSave={handleFieldSave}
                        />
                      </div>
                    </div>

                    {/* Address Information */}
                    <div
                      className={`border-t pt-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <MapPin size={18} />
                        Address Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <h4
                            className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                          >
                            Billing Address
                          </h4>
                          <EditableField
                            label="Street"
                            field="billingStreet"
                            value={account.billingStreet}
                            type="textarea"
                            onSave={handleFieldSave}
                          />
                          <div className="grid grid-cols-2 gap-4">
                            <EditableField
                              label="City"
                              field="billingCity"
                              value={account.billingCity}
                              onSave={handleFieldSave}
                            />
                            <EditableField
                              label="State"
                              field="billingState"
                              value={account.billingState}
                              onSave={handleFieldSave}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <EditableField
                              label="Postal Code"
                              field="billingPostalCode"
                              value={account.billingPostalCode}
                              onSave={handleFieldSave}
                            />
                            <EditableField
                              label="Country"
                              field="billingCountry"
                              value={account.billingCountry}
                              onSave={handleFieldSave}
                            />
                          </div>
                        </div>

                        <div className="space-y-4">
                          <h4
                            className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                          >
                            Shipping Address
                          </h4>
                          <EditableField
                            label="Street"
                            field="shippingStreet"
                            value={account.shippingStreet}
                            type="textarea"
                            onSave={handleFieldSave}
                          />
                          <div className="grid grid-cols-2 gap-4">
                            <EditableField
                              label="City"
                              field="shippingCity"
                              value={account.shippingCity}
                              onSave={handleFieldSave}
                            />
                            <EditableField
                              label="State"
                              field="shippingState"
                              value={account.shippingState}
                              onSave={handleFieldSave}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <EditableField
                              label="Postal Code"
                              field="shippingPostalCode"
                              value={account.shippingPostalCode}
                              onSave={handleFieldSave}
                            />
                            <EditableField
                              label="Country"
                              field="shippingCountry"
                              value={account.shippingCountry}
                              onSave={handleFieldSave}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <div
                      className={`border-t pt-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        Description
                      </h3>
                      <EditableField
                        label=""
                        field="description"
                        value={account.description}
                        type="textarea"
                        onSave={handleFieldSave}
                      />
                    </div>

                    {/* System Information */}
                    <div
                      className={`border-t pt-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        System Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <label
                            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Created By
                          </label>
                          <p
                            className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            <User size={16} />
                            {account.creator?.name || "System"}
                          </p>
                        </div>
                        <div>
                          <label
                            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Created Date
                          </label>
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {new Date(account.createdAt).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <label
                            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Last Modified By
                          </label>
                          <p
                            className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            <User size={16} />
                            {account.lastModifiedBy?.name || "—"}
                          </p>
                        </div>
                        <div>
                          <label
                            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Last Modified Date
                          </label>
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {new Date(account.updatedAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Related Tab */}
                {activeTab === "related" && (
                  <div className="space-y-6">
                    {/* Sub-tabs for Related */}
                    <div
                      className={`flex flex-wrap gap-2 border-b pb-2 mb-4 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                    >
                      {[
                        {
                          id: "contacts",
                          label: `Contacts (${contacts.length})`,
                          icon: Users,
                        },
                        {
                          id: "opportunities",
                          label: `Opportunities (${opportunities.length})`,
                          icon: DollarSign,
                        },
                        {
                          id: "projects",
                          label: `Projects (${projects.length})`,
                          icon: Briefcase,
                        },
                        {
                          id: "cases",
                          label: `Cases (${cases.length})`,
                          icon: AlertCircle,
                        },
                        {
                          id: "visits",
                          label: `Forms (${visits.length})`,
                          icon: Calendar,
                        },
                        {
                          id: "broadband",
                          label: `Broadband (${broadbandInfos.length})`,
                          icon: Wifi,
                        },
                        {
                          id: "lines",
                          label: `Lines (${lineInfos.length})`,
                          icon: PhoneCall,
                        },
                        {
                          id: "assets",
                          label: `Kit List (${assets.length})`,
                          icon: Package,
                        },
                        {
                          id: "phonesystems",
                          label: `Phone Systems (${phoneSystems.length})`,
                          icon: PhoneCall,
                        },
                        {
                          id: "tasks",
                          label: `Tasks (${tasks.length})`,
                          icon: CheckSquare,
                        },
                      ].map((tab) => {
                        const Icon = tab.icon;
                        return (
                          <button
                            key={tab.id}
                            onClick={() => setActiveRelatedTab(tab.id)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                              activeRelatedTab === tab.id
                                ? "bg-blue-600 text-white"
                                : isDarkMode
                                  ? "bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700"
                                  : "bg-gray-100 text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                            }`}
                          >
                            <div className="flex items-center gap-1.5">
                              <Icon size={14} />
                              {tab.label}
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Contacts */}
                    {activeRelatedTab === "contacts" && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3
                            className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            <Users size={18} />
                            Contacts ({contacts.length})
                          </h3>
                          <button
                            onClick={() => setShowCreateContact(true)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm"
                          >
                            <Plus size={16} />
                            New
                          </button>
                        </div>
                        {contacts.length === 0 ? (
                          <div
                            className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            <Users
                              size={48}
                              className="mx-auto mb-2 opacity-50"
                            />
                            <p>No contacts yet</p>
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead>
                                <tr
                                  className={`border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                                >
                                  <th
                                    className={`text-left py-3 px-3 text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    Name
                                  </th>
                                  <th
                                    className={`text-left py-3 px-3 text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    Title
                                  </th>
                                  <th
                                    className={`text-left py-3 px-3 text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    Email
                                  </th>
                                  <th
                                    className={`text-left py-3 px-3 text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    Phone
                                  </th>
                                  <th className="w-10"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {contacts.map((contact) => (
                                  <tr
                                    key={contact.id}
                                    className={`border-b transition-colors ${isDarkMode ? "border-gray-700/50 hover:bg-gray-800/30" : "border-gray-200/50 hover:bg-gray-50"}`}
                                  >
                                    <td className="py-3 px-3">
                                      <button
                                        onClick={() =>
                                          navigate(`/contacts/${contact.id}`)
                                        }
                                        className={`flex items-center gap-2 transition-colors ${isDarkMode ? "hover:text-blue-400" : "hover:text-blue-600"}`}
                                      >
                                        <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-white text-xs font-semibold">
                                          {getInitials(
                                            `${contact.firstName} ${contact.lastName}`,
                                          )}
                                        </div>
                                        <span
                                          className={`font-medium text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                        >
                                          {contact.firstName} {contact.lastName}
                                        </span>
                                      </button>
                                    </td>
                                    <td
                                      className={`py-3 px-3 text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                                    >
                                      {contact.title || "—"}
                                    </td>
                                    <td className="py-3 px-3">
                                      {contact.email ? (
                                        <a
                                          href={`mailto:${contact.email}`}
                                          onClick={(e) => e.stopPropagation()}
                                          className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                                        >
                                          {contact.email}
                                        </a>
                                      ) : (
                                        <span
                                          className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                                        >
                                          —
                                        </span>
                                      )}
                                    </td>
                                    <td className="py-3 px-3">
                                      {contact.phone ? (
                                        <a
                                          href={`tel:${contact.phone}`}
                                          onClick={(e) => e.stopPropagation()}
                                          className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                                        >
                                          {contact.phone}
                                        </a>
                                      ) : (
                                        <span
                                          className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                                        >
                                          —
                                        </span>
                                      )}
                                    </td>
                                    <td className="py-3 px-3">
                                      <button
                                        onClick={() =>
                                          navigate(`/contacts/${contact.id}`)
                                        }
                                        className={`transition-colors ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-400 hover:text-gray-900"}`}
                                      >
                                        <ExternalLink size={14} />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Opportunities */}
                    {activeRelatedTab === "opportunities" && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3
                            className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            <DollarSign size={18} />
                            Opportunities ({opportunities.length})
                          </h3>
                          <button
                            onClick={() => setShowCreateOpportunity(true)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm"
                          >
                            <Plus size={16} />
                            New
                          </button>
                        </div>
                        {opportunities.length === 0 ? (
                          <div
                            className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            <DollarSign
                              size={48}
                              className="mx-auto mb-2 opacity-50"
                            />
                            <p>No opportunities yet</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {opportunities.map((opp) => (
                              <div
                                key={opp.id}
                                onClick={() =>
                                  navigate(`/opportunities/${opp.id}`)
                                }
                                className={`p-3 rounded-lg transition-colors cursor-pointer flex items-center justify-between ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-50 hover:bg-gray-100"}`}
                              >
                                <div className="flex-1">
                                  <div
                                    className={`font-medium text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                  >
                                    {opp.name}
                                  </div>
                                  <div
                                    className={`flex items-center gap-3 mt-1 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    <span className="text-green-400">
                                      £{opp.amount?.toLocaleString() || "0"}
                                    </span>
                                    <span>•</span>
                                    <span className="capitalize">
                                      {opp.salesStatus?.replace("_", " ")}
                                    </span>
                                  </div>
                                </div>
                                <ExternalLink
                                  size={14}
                                  className={
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-400"
                                  }
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Projects */}
                    {activeRelatedTab === "projects" && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3
                            className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            <Briefcase size={18} />
                            Projects ({projects.length})
                          </h3>
                          <button
                            onClick={() => setShowCreateProject(true)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm"
                          >
                            <Plus size={16} />
                            New
                          </button>
                        </div>
                        {projects.length === 0 ? (
                          <div
                            className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            <Briefcase
                              size={48}
                              className="mx-auto mb-2 opacity-50"
                            />
                            <p>No projects yet</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {projects.map((project) => (
                              <div
                                key={project.id}
                                onClick={() =>
                                  navigate(`/projects/${project.id}`)
                                }
                                className={`p-3 rounded-lg transition-colors cursor-pointer flex items-center justify-between ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-50 hover:bg-gray-100"}`}
                              >
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span
                                      className={`font-medium text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                    >
                                      {project.projectName}
                                    </span>
                                    <span
                                      className={`text-xs px-2 py-0.5 rounded ${
                                        project.status === "Complete"
                                          ? "bg-green-500/20 text-green-400"
                                          : "bg-blue-500/20 text-blue-400"
                                      }`}
                                    >
                                      {project.status}
                                    </span>
                                  </div>
                                  <div
                                    className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    {project.projectNumber}
                                  </div>
                                </div>
                                <ExternalLink
                                  size={14}
                                  className={
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-400"
                                  }
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Cases */}
                    {activeRelatedTab === "cases" && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3
                            className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            <AlertCircle size={18} />
                            Cases ({cases.length})
                          </h3>
                          <button
                            onClick={() => setShowCreateCase(true)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm"
                          >
                            <Plus size={16} />
                            New
                          </button>
                        </div>
                        {cases.length === 0 ? (
                          <div
                            className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            <AlertCircle
                              size={48}
                              className="mx-auto mb-2 opacity-50"
                            />
                            <p>No cases yet</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {cases.map((caseItem) => (
                              <div
                                key={caseItem.id}
                                onClick={() =>
                                  navigate(`/cases/${caseItem.id}`)
                                }
                                className={`p-3 rounded-lg transition-colors cursor-pointer flex items-center justify-between ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-50 hover:bg-gray-100"}`}
                              >
                                <div className="flex-1">
                                  <div
                                    className={`font-medium text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                  >
                                    {caseItem.subject}
                                  </div>
                                  <div
                                    className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    {caseItem.caseNumber} • {caseItem.status}
                                  </div>
                                </div>
                                <ExternalLink
                                  size={14}
                                  className={
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-400"
                                  }
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Visits */}
                    {activeRelatedTab === "visits" && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3
                            className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            <Calendar size={18} />
                            Forms ({visits.length})
                          </h3>
                          <button
                            onClick={() => setShowCreateVisit(true)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm"
                          >
                            <Plus size={16} />
                            New
                          </button>
                        </div>
                        {visits.length === 0 ? (
                          <div
                            className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            <Calendar
                              size={48}
                              className="mx-auto mb-2 opacity-50"
                            />
                            <p>No forms yet</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {visits.map((visit) => (
                              <div
                                key={visit.id}
                                onClick={() => navigate(`/visits/${visit.id}`)}
                                className={`p-3 rounded-lg transition-colors cursor-pointer flex items-center justify-between ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-50 hover:bg-gray-100"}`}
                              >
                                <div className="flex-1">
                                  <div
                                    className={`font-medium text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                  >
                                    {visit.visitName}
                                  </div>
                                  <div
                                    className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    {new Date(
                                      visit.visitDate,
                                    ).toLocaleDateString()}{" "}
                                    • {visit.status}
                                  </div>
                                </div>
                                <ExternalLink
                                  size={14}
                                  className={
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-400"
                                  }
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Broadband */}
                    {activeRelatedTab === "broadband" && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3
                            className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            <Wifi size={18} />
                            Broadband Information ({broadbandInfos.length})
                          </h3>
                          <button
                            onClick={() => setShowCreateBroadband(true)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm"
                          >
                            <Plus size={16} />
                            New
                          </button>
                        </div>
                        {broadbandInfos.length === 0 ? (
                          <div
                            className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            <Wifi
                              size={48}
                              className="mx-auto mb-2 opacity-50"
                            />
                            <p>No broadband information yet</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {broadbandInfos.map((info) => (
                              <div
                                key={info.id}
                                className={`p-3 rounded-lg transition-colors ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-50 hover:bg-gray-100"}`}
                                onClick={() =>
                                  navigate(`/broadband-information/${info.id}`)
                                }
                              >
                                <div className="flex-1">
                                  <div
                                    className={`font-medium text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                  >
                                    {info.type || "Broadband"}
                                  </div>
                                  <div
                                    className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    {info.broadbandSupplier} •{" "}
                                    {info.expectedSpeeds}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Lines */}
                    {activeRelatedTab === "lines" && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3
                            className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            <PhoneCall size={18} />
                            Line Information ({lineInfos.length})
                          </h3>
                          <button
                            onClick={() => setShowCreateLineInfo(true)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm"
                          >
                            <Plus size={16} />
                            New
                          </button>
                        </div>
                        {lineInfos.length === 0 ? (
                          <div
                            className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            <PhoneCall
                              size={48}
                              className="mx-auto mb-2 opacity-50"
                            />
                            <p>No line information yet</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {lineInfos.map((line) => (
                              <div
                                key={line.id}
                                className={`p-3 rounded-lg transition-colors ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-50 hover:bg-gray-100"}`}
                              >
                                <div className="flex-1">
                                  <div
                                    className={`font-medium text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                  >
                                    {line.lineType || "Line"}
                                  </div>
                                  <div
                                    className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    {line.sipNumber} • {line.usage}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Assets */}
                    {activeRelatedTab === "assets" && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3
                            className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            <Package size={18} />
                            Assets ({assets.length})
                          </h3>
                          <button
                            onClick={() => setShowCreateAsset(true)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm"
                          >
                            <Plus size={16} />
                            New
                          </button>
                        </div>
                        {assets.length === 0 ? (
                          <div
                            className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            <Package
                              size={48}
                              className="mx-auto mb-2 opacity-50"
                            />
                            <p>No assets yet</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {assets.map((asset) => (
                              <div
                                key={asset.id}
                                onClick={() => navigate(`/assets/${asset.id}`)}
                                className={`p-3 rounded-lg transition-colors cursor-pointer flex items-center justify-between ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-50 hover:bg-gray-100"}`}
                              >
                                <div className="flex-1">
                                  <div
                                    className={`font-medium text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                  >
                                    {asset.assetName}
                                  </div>
                                  <div
                                    className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    {asset.serialNumber} • Qty: {asset.quantity}
                                  </div>
                                </div>
                                <ExternalLink
                                  size={14}
                                  className={
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-400"
                                  }
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {activeRelatedTab === "phonesystems" && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3
                            className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            <PhoneCall size={18} />
                            Phone Systems ({phoneSystems.length})
                          </h3>
                          <button
                            onClick={() => setShowCreatePhoneSystem(true)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm"
                          >
                            <Plus size={16} />
                            New
                          </button>
                        </div>
                        {phoneSystems.length === 0 ? (
                          <div
                            className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            <PhoneCall
                              size={48}
                              className="mx-auto mb-2 opacity-50"
                            />
                            <p>No phone systems yet</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {phoneSystems.map((system) => (
                              <div
                                key={system.id}
                                onClick={() =>
                                  navigate(
                                    `/accounts/${id}/phone-systems/${system.id}`,
                                  )
                                }
                                className={`p-4 rounded-lg transition-colors cursor-pointer ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-50 hover:bg-gray-100"}`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                      <PhoneCall
                                        size={16}
                                        className="text-blue-400"
                                      />
                                      <span
                                        className={`font-medium text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                      >
                                        {system.systemType}
                                      </span>
                                      {system.licenses && (
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400">
                                          {system.licenses} licenses
                                        </span>
                                      )}
                                    </div>
                                    {system.notes && (
                                      <p
                                        className={`text-sm mt-2 whitespace-pre-wrap line-clamp-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                      >
                                        {system.notes}
                                      </p>
                                    )}
                                    <div
                                      className={`flex items-center gap-3 mt-3 text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                                    >
                                      {system.creator && (
                                        <div className="flex items-center gap-1">
                                          <User size={12} />
                                          <span>{system.creator.name}</span>
                                        </div>
                                      )}
                                      <span>•</span>
                                      <span>
                                        {new Date(
                                          system.createdAt,
                                        ).toLocaleDateString()}
                                      </span>
                                    </div>
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(
                                        `/accounts/${id}/phone-systems/${system.id}`,
                                      );
                                    }}
                                    className={`transition-colors p-2 ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-400 hover:text-gray-900"}`}
                                  >
                                    <ExternalLink size={16} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {/* Tasks */}
                    {activeRelatedTab === "tasks" && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3
                            className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            <CheckSquare size={18} />
                            Tasks ({tasks.length})
                          </h3>
                          <button
                            onClick={() => setShowCreateTask(true)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm"
                          >
                            <Plus size={16} />
                            New Task
                          </button>
                        </div>
                        {tasks.length === 0 ? (
                          <div
                            className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            <CheckSquare
                              size={48}
                              className="mx-auto mb-2 opacity-50"
                            />
                            <p>No tasks yet</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {tasks.map((task) => (
                              <div
                                key={task.id}
                                className={`rounded-lg p-4 transition-colors cursor-pointer ${isDarkMode ? "bg-gray-800/30 hover:bg-gray-800/50" : "bg-gray-50 hover:bg-gray-100"}`}
                                onClick={() => navigate(`/tasks/${task.id}`)}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-2">
                                      <h4
                                        className={`font-medium text-sm truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                      >
                                        {task.title}
                                      </h4>
                                      {/* Status Badge */}
                                      <span
                                        className={`text-xs px-2 py-0.5 rounded-full ${
                                          task.status === "completed"
                                            ? "bg-green-500/20 text-green-400"
                                            : task.status === "in_progress"
                                              ? "bg-blue-500/20 text-blue-400"
                                              : task.status === "blocked"
                                                ? "bg-red-500/20 text-red-400"
                                                : task.status === "review"
                                                  ? "bg-purple-500/20 text-purple-400"
                                                  : "bg-gray-500/20 text-gray-400"
                                        }`}
                                      >
                                        {task.status === "in_progress"
                                          ? "In Progress"
                                          : task.status === "todo"
                                            ? "To Do"
                                            : task.status
                                                .charAt(0)
                                                .toUpperCase() +
                                              task.status.slice(1)}
                                      </span>
                                      {/* Priority Badge */}
                                      {task.priority && (
                                        <span
                                          className={`text-xs px-2 py-0.5 rounded-full ${
                                            task.priority === "urgent"
                                              ? "bg-red-500/20 text-red-400"
                                              : task.priority === "high"
                                                ? "bg-orange-500/20 text-orange-400"
                                                : task.priority === "medium"
                                                  ? "bg-yellow-500/20 text-yellow-400"
                                                  : "bg-gray-500/20 text-gray-400"
                                          }`}
                                        >
                                          {task.priority
                                            .charAt(0)
                                            .toUpperCase() +
                                            task.priority.slice(1)}
                                        </span>
                                      )}
                                    </div>
                                    {task.description && (
                                      <p
                                        className={`text-sm line-clamp-2 mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                      >
                                        {task.description}
                                      </p>
                                    )}
                                    <div
                                      className={`flex items-center gap-4 text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                                    >
                                      {task.assignedUser && (
                                        <div className="flex items-center gap-1.5">
                                          <User size={12} />
                                          <span>{task.assignedUser.name}</span>
                                        </div>
                                      )}
                                      {task.dueDate && (
                                        <div className="flex items-center gap-1.5">
                                          <Calendar size={12} />
                                          <span>
                                            Due:{" "}
                                            {new Date(
                                              task.dueDate,
                                            ).toLocaleDateString()}
                                          </span>
                                        </div>
                                      )}
                                      {task.progress !== undefined &&
                                        task.progress > 0 && (
                                          <div className="flex items-center gap-1.5">
                                            <span>
                                              {task.progress}% complete
                                            </span>
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/tasks/${task.id}`);
                                    }}
                                    className={`transition-colors p-1 ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-400 hover:text-gray-900"}`}
                                  >
                                    <ExternalLink size={14} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Appointments moved to main tab */}
                    {activeRelatedTab === "appointments_removed" && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3
                            className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            <Calendar size={18} />
                            Appointments ({appointments.length})
                          </h3>
                        </div>
                        {appointments.length === 0 ? (
                          <div
                            className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            <Calendar
                              size={48}
                              className="mx-auto mb-2 opacity-50"
                            />
                            <p>No appointments yet</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {appointments.map((appt) => {
                              const statusColors = {
                                scheduled: "bg-blue-500/20 text-blue-400",
                                confirmed: "bg-green-500/20 text-green-400",
                                completed: "bg-gray-500/20 text-gray-400",
                                cancelled: "bg-red-500/20 text-red-400",
                                no_show: "bg-orange-500/20 text-orange-400",
                              };
                              const typeLabels = {
                                meeting: "Meeting",
                                call: "Call",
                                site_visit: "Site Visit",
                                consultation: "Consultation",
                                demo: "Demo",
                                follow_up: "Follow Up",
                                other: "Other",
                              };
                              return (
                                <div
                                  key={appt.id}
                                  onClick={() =>
                                    navigate(`/appointments/${appt.id}`)
                                  }
                                  className={`rounded-lg p-4 transition-colors cursor-pointer ${isDarkMode ? "bg-gray-800/30 hover:bg-gray-800/50" : "bg-gray-50 hover:bg-gray-100"}`}
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1">
                                        <h4
                                          className={`font-medium text-sm truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                        >
                                          {appt.title}
                                        </h4>
                                        <span
                                          className={`text-xs px-2 py-0.5 rounded-full ${
                                            statusColors[appt.status] ||
                                            "bg-gray-500/20 text-gray-400"
                                          }`}
                                        >
                                          {appt.status.charAt(0).toUpperCase() +
                                            appt.status.slice(1)}
                                        </span>
                                        {appt.type && (
                                          <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">
                                            {typeLabels[appt.type] || appt.type}
                                          </span>
                                        )}
                                      </div>
                                      <div
                                        className={`flex flex-wrap items-center gap-3 text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                      >
                                        <div className="flex items-center gap-1.5">
                                          <Calendar size={12} />
                                          <span>
                                            {new Date(
                                              appt.startTime,
                                            ).toLocaleString([], {
                                              dateStyle: "medium",
                                              timeStyle: "short",
                                            })}
                                          </span>
                                        </div>
                                        {appt.assignee && (
                                          <div className="flex items-center gap-1.5">
                                            <User size={12} />
                                            <span>{appt.assignee.name}</span>
                                          </div>
                                        )}
                                        {appt.location && (
                                          <div className="flex items-center gap-1.5">
                                            <MapPin size={12} />
                                            <span className="truncate max-w-[200px]">
                                              {appt.location}
                                            </span>
                                          </div>
                                        )}
                                        {appt.opportunity && (
                                          <div className="flex items-center gap-1.5 text-blue-400">
                                            <DollarSign size={12} />
                                            <span>{appt.opportunity.name}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/appointments/${appt.id}`);
                                      }}
                                      className={`transition-colors p-1 ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-400 hover:text-gray-900"}`}
                                    >
                                      <ExternalLink size={14} />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Appointments Tab */}
                {activeTab === "appointments" && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3
                        className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <Calendar size={18} />
                        Appointments ({appointments.length})
                      </h3>
                    </div>
                    {appointments.length === 0 ? (
                      <div
                        className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        <Calendar
                          size={48}
                          className="mx-auto mb-2 opacity-50"
                        />
                        <p>No appointments yet</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {appointments.map((appt) => {
                          const statusColors = {
                            scheduled: "bg-blue-500/20 text-blue-400",
                            confirmed: "bg-green-500/20 text-green-400",
                            completed: "bg-gray-500/20 text-gray-400",
                            cancelled: "bg-red-500/20 text-red-400",
                            no_show: "bg-orange-500/20 text-orange-400",
                          };
                          const typeLabels = {
                            meeting: "Meeting",
                            call: "Call",
                            site_visit: "Site Visit",
                            consultation: "Consultation",
                            demo: "Demo",
                            follow_up: "Follow Up",
                            other: "Other",
                          };
                          return (
                            <div
                              key={appt.id}
                              onClick={() =>
                                navigate(`/appointments/${appt.id}`)
                              }
                              className={`rounded-lg p-4 transition-colors cursor-pointer ${isDarkMode ? "bg-gray-800/30 hover:bg-gray-800/50" : "bg-gray-50 hover:bg-gray-100"}`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h4
                                      className={`font-medium text-sm truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                    >
                                      {appt.title}
                                    </h4>
                                    <span
                                      className={`text-xs px-2 py-0.5 rounded-full ${
                                        statusColors[appt.status] ||
                                        "bg-gray-500/20 text-gray-400"
                                      }`}
                                    >
                                      {appt.status.charAt(0).toUpperCase() +
                                        appt.status.slice(1)}
                                    </span>
                                    {appt.type && (
                                      <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">
                                        {typeLabels[appt.type] || appt.type}
                                      </span>
                                    )}
                                  </div>
                                  <div
                                    className={`flex flex-wrap items-center gap-3 text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    <div className="flex items-center gap-1.5">
                                      <Calendar size={12} />
                                      <span>
                                        {new Date(
                                          appt.startTime,
                                        ).toLocaleString([], {
                                          dateStyle: "medium",
                                          timeStyle: "short",
                                        })}
                                      </span>
                                    </div>
                                    {appt.assignee && (
                                      <div className="flex items-center gap-1.5">
                                        <User size={12} />
                                        <span>{appt.assignee.name}</span>
                                      </div>
                                    )}
                                    {appt.location && (
                                      <div className="flex items-center gap-1.5">
                                        <MapPin size={12} />
                                        <span className="truncate max-w-[200px]">
                                          {appt.location}
                                        </span>
                                      </div>
                                    )}
                                    {appt.opportunity && (
                                      <div className="flex items-center gap-1.5 text-blue-400">
                                        <DollarSign size={12} />
                                        <span>{appt.opportunity.name}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/appointments/${appt.id}`);
                                  }}
                                  className={`transition-colors p-1 ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-400 hover:text-gray-900"}`}
                                >
                                  <ExternalLink size={14} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                {activeTab === "calls" && (
                  <div
                    className={`border rounded-lg p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
                  >
                    <h2
                      className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      Call History
                    </h2>
                    <CallNotesTimeline
                      entityType="account" // or "contact", "case", "account"
                      entityId={id}
                    />
                  </div>
                )}
                {activeTab === "files" && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3
                        className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <Paperclip size={18} />
                        Files ({files.length})
                      </h3>
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          onChange={handleFileUpload}
                          className="hidden"
                        />
                        <div className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm transition-colors">
                          <Plus size={16} />
                          Upload File
                        </div>
                      </label>
                    </div>

                    {files.length === 0 ? (
                      <div
                        className={`text-center py-12 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        <Paperclip
                          size={64}
                          className="mx-auto mb-4 opacity-30"
                        />
                        <p className="text-lg mb-2">No files attached</p>
                        <p className="text-sm mb-6">
                          Upload files to get started
                        </p>
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            onChange={handleFileUpload}
                            className="hidden"
                          />
                          <div className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
                            <Plus size={18} />
                            Upload First File
                          </div>
                        </label>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {files.map((file) => (
                          <div
                            key={file.id}
                            className={`group flex items-center gap-4 p-4 rounded-lg transition-colors border ${isDarkMode ? "bg-gray-800/30 hover:bg-gray-800/50 border-gray-700/50" : "bg-gray-50 hover:bg-gray-100 border-gray-200/50"}`}
                          >
                            <div className="flex-shrink-0">
                              {getFileIconComponent(file.fileType)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div
                                className={`font-medium text-sm truncate mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                title={file.title}
                              >
                                {file.title}
                              </div>
                              <div
                                className={`flex items-center gap-3 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                              >
                                <span className="flex items-center gap-1">
                                  <Package size={12} />
                                  {formatFileSize(file.contentSize)}
                                </span>
                                <span>•</span>
                                <span className="flex items-center gap-1">
                                  <Calendar size={12} />
                                  {getFileCreatedDate(
                                    file,
                                  ).toLocaleDateString()}
                                </span>
                                {file.creator && (
                                  <>
                                    <span>•</span>
                                    <span className="flex items-center gap-1">
                                      <User size={12} />
                                      {file.creator.name}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              {file.fileUrl && (
                                <a
                                  href={getFullFileUrl(file.fileUrl)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"}`}
                                  title="Download"
                                >
                                  <ExternalLink
                                    size={18}
                                    className="text-blue-400"
                                  />
                                </a>
                              )}
                              <button
                                onClick={() => handleDeleteFile(file.id)}
                                className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"}`}
                                title="Delete"
                              >
                                <Trash2 size={18} className="text-red-400" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Emails Tab */}
                {activeTab === "emails" && (
                  <div
                    className={`border rounded-lg p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
                  >
                    <div className="flex items-center justify-between mb-6">
                      <h2
                        className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        Emails
                      </h2>
                      <button
                        onClick={() => setShowSendEmail(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Send Email
                      </button>
                    </div>
                    <AccountEmailThread accountId={id} accountData={account} />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar - Only Notes Now */}
          <div className="space-y-6">
            <AccountDuplicatesSidebar
              accountId={id}
              accountName={account.name}
            />
            {/* Notes */}
            <div
              className={`rounded-lg border p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <div
                className={`flex items-center justify-between mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                <h3 className="font-semibold">Notes ({notes.length})</h3>
              </div>

              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Note title (optional)"
                  value={newNoteTitle}
                  onChange={(e) => setNewNoteTitle(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg mb-2 focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                />
                <textarea
                  placeholder="Add a note..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg resize-none focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                />
                <div className="flex items-center justify-between mt-2">
                  <label
                    className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    <input
                      type="checkbox"
                      checked={isPrivateNote}
                      onChange={(e) => setIsPrivateNote(e.target.checked)}
                      className="rounded"
                    />
                    Private
                  </label>
                  <button
                    onClick={handleCreateNote}
                    disabled={!newNote.trim()}
                    className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add Note
                  </button>
                </div>
              </div>

              <div className="space-y-3 max-h-96 overflow-y-auto">
                {notes.map((note) => (
                  <div
                    key={note.id}
                    className={`p-3 rounded-lg ${isDarkMode ? "bg-gray-800/50" : "bg-gray-50"}`}
                  >
                    {note.title && (
                      <div
                        className={`font-medium text-sm mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {note.title}
                      </div>
                    )}
                    <p
                      className={`text-sm whitespace-pre-wrap ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                    >
                      {note.body}
                    </p>
                    <div
                      className={`flex items-center justify-between mt-2 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      <span>{note.creator?.name}</span>
                      <span>
                        {new Date(note.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
                {notes.length === 0 && (
                  <p
                    className={`text-sm text-center py-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    No notes yet
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showEditModal && (
        <EditAccountModal
          account={account}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            fetchAccount();
          }}
        />
      )}

      {showCreateContact && (
        <CreateContactModal
          account={account}
          onClose={() => setShowCreateContact(false)}
          onSuccess={() => {
            setShowCreateContact(false);
            fetchAccount();
          }}
        />
      )}

      {showCreateOpportunity && (
        <CreateOpportunityModal
          account={account}
          users={users}
          onClose={() => setShowCreateOpportunity(false)}
          onSuccess={() => {
            setShowCreateOpportunity(false);
            fetchAccount();
          }}
        />
      )}

      {showCreateProject && (
        <CreateProjectModal
          account={account}
          onClose={() => setShowCreateProject(false)}
          onSuccess={() => {
            setShowCreateProject(false);
            fetchProjects();
          }}
        />
      )}

      {showCreateCase && (
        <CreateCaseModal
          account={account}
          onClose={() => setShowCreateCase(false)}
          onSuccess={() => {
            setShowCreateCase(false);
            fetchAccount();
          }}
        />
      )}

      {showCreateVisit && (
        <CreateVisitModal
          account={account}
          onClose={() => setShowCreateVisit(false)}
          onSuccess={() => {
            setShowCreateVisit(false);
            fetchVisits();
          }}
        />
      )}

      {showCreateBroadband && (
        <CreateBroadbandInfoModal
          account={account}
          onClose={() => setShowCreateBroadband(false)}
          onSuccess={() => {
            setShowCreateBroadband(false);
            fetchBroadbandInfos();
          }}
        />
      )}

      {showCreateLineInfo && (
        <CreateLineInfoModal
          account={account}
          onClose={() => setShowCreateLineInfo(false)}
          onSuccess={() => {
            setShowCreateLineInfo(false);
            fetchLineInfos();
          }}
        />
      )}

      {showCreateAsset && (
        <CreateAssetModal
          account={account}
          onClose={() => setShowCreateAsset(false)}
          onSuccess={() => {
            setShowCreateAsset(false);
            fetchAssets();
          }}
        />
      )}

      {showBookInstall && (
        <BookInstallModal
          preSelectedAccount={account}
          onClose={() => setShowBookInstall(false)}
          onSuccess={() => {
            setShowBookInstall(false);
            fetchVisits();
          }}
        />
      )}

      {showCreateTask && (
        <CreateTaskModal
          account={account}
          onClose={() => setShowCreateTask(false)}
          onSuccess={() => {
            setShowCreateTask(false);
            fetchTasks();
          }}
        />
      )}

      {/* Drag and Drop Overlay */}
      {isDragging && (
        <div className="fixed inset-0 bg-blue-500/20 backdrop-blur-sm flex items-center justify-center z-50 pointer-events-none">
          <div
            className={`rounded-lg border-2 border-dashed border-blue-500 p-8 text-center ${isDarkMode ? "bg-[#13161B]" : "bg-white"}`}
          >
            <Paperclip size={48} className="mx-auto mb-4 text-blue-400" />
            <p
              className={`text-xl font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Drop files to upload
            </p>
            <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
              Release to add files to this account
            </p>
          </div>
        </div>
      )}

      {/* Upload Confirmation Modal */}
      {showUploadConfirmation && filesToUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className={`rounded-lg border max-w-md w-full p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Paperclip className="text-blue-400" size={24} />
              </div>
              <div className="flex-1">
                <h3
                  className={`text-lg font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Upload Files?
                </h3>
                <p
                  className={`text-sm mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {filesToUpload.length === 1
                    ? `Upload "${filesToUpload[0].name}" to this account?`
                    : `Upload ${filesToUpload.length} files to this account?`}
                </p>

                {filesToUpload.length > 1 && (
                  <div className="space-y-1 mb-4 max-h-40 overflow-y-auto">
                    {Array.from(filesToUpload).map((file, idx) => (
                      <div
                        key={idx}
                        className={`text-sm flex items-center gap-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        <Paperclip size={14} />
                        <span className="truncate">{file.name}</span>
                        <span className="text-xs">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={cancelUpload}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              >
                Cancel
              </button>
              <button
                onClick={confirmUpload}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {showSendEmail && (
        <SendAccountEmailModal
          accountData={account}
          onClose={() => setShowSendEmail(false)}
          onSuccess={() => {
            setShowSendEmail(false);
            fetchAccount();
          }}
        />
      )}

      {showCreatePhoneSystem && (
        <CreatePhoneSystemModal
          accountId={id}
          onClose={() => setShowCreatePhoneSystem(false)}
          onSuccess={() => {
            setShowCreatePhoneSystem(false);
            fetchPhoneSystems();
          }}
        />
      )}
    </div>
  );
};

export default AccountDetail;
